import { useState, ChangeEvent } from 'react';
// Ui3
import Button from 'ui3/Button/Button';
import Modal from 'ui3/Modal/Modal';
import TextArea from 'ui3/TextArea/TextArea';
// Styles
import styles from './VideoFeedbackModal.module.scss';

type VideoFeedbackModalProps = {
  isOpen: boolean;
  isApprove: boolean;
  isMultiple?: boolean;
  onClose: () => void;
  sendFeedback: (feedback: string, rating?: number | null) => void;
};

enum Rating {
  Terrible = '1',
  Passable = '2',
  Fine = '3',
  Good = '4',
  Excellent = '5',
}

const VideoFeedbackModal = ({
  isOpen,
  isApprove,
  isMultiple = false,
  onClose,
  sendFeedback,
}: VideoFeedbackModalProps): JSX.Element | null => {
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState(Rating.Excellent);

  const resetState = () => {
    setFeedback('');
    setRating(Rating.Excellent);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFeedbackChange = (e: ChangeEvent<HTMLTextAreaElement>) =>
    setFeedback(e.target.value);

  const handleRatingChange = (e: ChangeEvent<HTMLInputElement>) =>
    setRating(e.target.value as Rating);

  const handleSendFeedback = () => {
    sendFeedback(feedback, isApprove ? +rating : undefined);
    resetState();
  };

  const placeholderText = isApprove
    ? 'Your feedback is invaluable to us.'
    : 'Please provide your feedback on what needs to be corrected or edited in the video. If no feedback is given, the video won’t be posted on your media feed.';

  const titleText = isApprove
    ? 'Thank you for your approval'
    : 'Help us improve the video';

  const subtitleText = isApprove
    ? isMultiple
      ? "We'd love to hear your thoughts. Please rate the videos below and let us know what you liked or any suggestions for improvement."
      : "We'd love to hear your thoughts. Please rate the video below and let us know what you liked or any suggestions for improvement."
    : 'Your feedback will guide us in making the necessary changes';

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={titleText}
      subTitle={subtitleText}
    >
      <div className={styles.root}>
        {isApprove && (
          <div className={styles.approveRateWrapper}>
            <fieldset className={styles.rating}>
              <div className={styles.ratingGroup}>
                {Object.entries(Rating).map(([label, value]) => (
                  <input
                    key={value}
                    className={styles.ratingStar}
                    type="radio"
                    name="feedback"
                    value={value}
                    aria-label={label}
                    onChange={handleRatingChange}
                    checked={rating === value}
                  />
                ))}
              </div>
            </fieldset>
          </div>
        )}

        <TextArea
          name="videoFeedback"
          placeholder={placeholderText}
          className={styles.textarea}
          rows={4}
          value={feedback}
          onChange={handleFeedbackChange}
          maxLength={256}
        />

        <Button
          className={styles.actionButtons}
          variant="secondary"
          size="medium"
          onClick={handleSendFeedback}
        >
          {isApprove ? 'Send' : 'Send Feedback'}
        </Button>
      </div>
    </Modal>
  );
};

export default VideoFeedbackModal;
